import React, { useState, useEffect, useMemo } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Users, ArrowLeft, RefreshCw, LogOut, Lock, Calendar, Clock, History, Zap, Search, Download, ChevronDown } from 'lucide-react';
import axios from 'axios';
import './App.css';

// --- API CONFIGURATION ---
const API_BASE_URL = "https://ethiccraft.org/backend/Scanner_Events"; 

const ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/Login.php`,
  GET_EVENTS: `${API_BASE_URL}/Event_Details.php`,
  VERIFY_TICKET: `${API_BASE_URL}/verify_qr.php`,
  GET_ATTENDEES: `${API_BASE_URL}/Get_Attendees.php`
};

// --- DATE UTILS ---
const getNormalizedDate = (dateString) => {
    // Returns a Date object set to midnight for accurate comparison
    const d = new Date(dateString);
    d.setHours(0, 0, 0, 0);
    return d;
};

const formatDateDisplay = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isToday = (eventDateString) => {
  const today = new Date();
  const event = new Date(eventDateString);
  return (
    today.getDate() === event.getDate() &&
    today.getMonth() === event.getMonth() &&
    today.getFullYear() === event.getFullYear()
  );
};

const isPast = (eventDateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const event = getNormalizedDate(eventDateString);
    return event < today;
};

const HistorySpreadsheet = ({ event, onBack }) => {
    const [attendees, setAttendees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!event?.id) return; // Guard clause
            
            setLoading(true);
            setError(null);
            
            try {
                // Fetching attendees SPECIFIC to this event ID
                console.log(`Fetching attendees for Event ID: ${event.id} (${event.name})`);
                const response = await axios.get(`${ENDPOINTS.GET_ATTENDEES}?event_id=${event.id}`);
                
                if (Array.isArray(response.data)) {
                    setAttendees(response.data);
                } else {
                    setAttendees([]); // Handle empty or malformed response
                }
            } catch (err) {
                console.error("Error fetching attendees:", err);
                setError("Failed to load data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [event.id]); // Dependency array ensures re-fetch on event change

    // Live Filter Logic
    const filteredData = useMemo(() => {
        return attendees.filter(student => 
            (student.name && student.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.id && String(student.id).toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [attendees, searchTerm]);

    return (
        <motion.div 
            className="spreadsheet-view"
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: 20 }}
        >
            {/* Header Section */}
            <div className="sheet-header">
                <button className="back-icon-btn" onClick={onBack}><ArrowLeft /></button>
                <div className="sheet-title-group">
                    <h2>{event.name}</h2>
                    <div className="sheet-meta">
                        <span><Calendar size={12}/> {formatDateDisplay(event.date)}</span>
                        <span className="dot">•</span>
                        <span>{attendees.length} Attendees</span>
                    </div>
                </div>
                <button className="export-btn"><Download size={16} /> Export</button>
            </div>

            {/* Toolbar */}
            <div className="sheet-toolbar">
                <div className="search-bar">
                    <Search size={16} className="search-icon"/>
                    <input 
                        type="text" 
                        placeholder="Search by Name or ID..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* The Data Grid */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-spinner">Loading Data...</div>
                ) : (
                    <table className="premium-table">
                        <thead>
                            <tr>
                                <th width="60px">#</th>
                                <th>Student Name</th>
                                <th>Course / ID</th>
                                <th>Graduation_Year</th>
                                <th>Gender</th>
                                <th>Check-in Time</th>
                                <th className="text-right">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length > 0 ? filteredData.map((student, index) => (
                                <tr key={index}>
                                    <td className="row-index">{index + 1}</td>
                                    <td>
                                        <div className="table-user-cell">
                                            <div className="table-avatar">{student.name.charAt(0)}</div>
                                            <span className="table-name">{student.name}</span>
                                        </div>
                                    </td>
                                    <td className="text-muted small-text">
                                        {student.course || "N/A"} <br/>
                                        <span className="id-pill">{student.id || "---"}</span>
                                    </td>
                                    <td className="text-muted">{student.year || "N/A"}</td>
                                    <td className="text-muted">{student.gender || "N/A"}</td>
                                    <td className="text-muted">{student.time || "N/A"}</td>
                                    <td className="text-right">
                                        <span className="status-pill present">Present</span>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="empty-row">No records found matching "{searchTerm}"</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </motion.div>
    );
};

// --- COMPONENT: LOGIN SCREEN ---
const LoginScreen = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(ENDPOINTS.LOGIN, { email, password });
      
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token || 'logged_in');
        onLoginSuccess();
      } else {
        setError(response.data.message || "Invalid credentials. Access Denied.");
      }
    } catch (err) {
      console.error("Login Error", err);
      // Demo fallback
      if (email === "admin@ethiccraft.org" && password === "admin123") {
         localStorage.setItem('admin_token', 'demo_token');
         onLoginSuccess();
         return;
      }
      setError("Server Error. Please check connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-icon"><Lock size={40} /></div>
        <h2>Admin Access</h2>
        <p>Please log in to access the scanner.</p>
        <form onSubmit={handleLogin}>
          <div className="input-group">
            <input type="email" placeholder="Email ID" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="input-group">
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="login-btn" disabled={loading}>{loading ? "Verifying..." : "Login"}</button>
        </form>
      </div>
    </div>
  );
};

// --- COMPONENT: MAIN SCANNER DASHBOARD ---
const ScannerDashboard = ({ onLogout }) => {
  const [view, setView] = useState('events'); 
  const [events, setEvents] = useState([]);
  
  // New State for Tab Switching
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'history'

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [attendedList, setAttendedList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [pauseScan, setPauseScan] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
        try {
          setLoading(true);
          const response = await axios.get(ENDPOINTS.GET_EVENTS);
          if (Array.isArray(response.data)) {
             setEvents(response.data);
          } else {
             // Mock data includes past dates for demonstration
             setEvents([
               { id: 1, name: "Tech Summit 2025", date: "2025-12-25" }, 
               { id: 2, name: "Ethiccraft Camp", date: "2025-12-20" },
               { id: 3, name: "Today's Test Event", date: new Date().toISOString().split('T')[0] },
               { id: 4, name: "Old Workshop 2023", date: "2023-05-10" },
               { id: 5, name: "Summer Camp 2024", date: "2024-06-15" }
             ]);
          }
        } catch (error) {
          setErrorMsg("Could not load events.");
        } finally {
          setLoading(false);
        }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
      const fetchEvents = async () => {
          try {
            const response = await axios.get(ENDPOINTS.GET_EVENTS);
            if (Array.isArray(response.data)) setEvents(response.data);
            else setEvents([{ id: 1, name: "Tech Summit", date: "2025-12-25" }, { id: 2, name: "Old Event", date: "2023-01-01" }]);
          } catch(e) {}
      };
      fetchEvents();
  }, []);

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    
    if (isPast(event.date)) {
        // If it's a past event -> Open Spreadsheet directly
        setView('history_sheet');
    } else {
        // If it's upcoming/today -> Open Scanner
        setView('scanner');
        setPauseScan(false);
        setScanResult(null);
    }
  };

  const handleScan = async (detectedCodes) => {
    if (pauseScan || !detectedCodes || detectedCodes.length === 0) return;
    const scannedCode = detectedCodes[0].rawValue;
    setPauseScan(true); 

    // DATE VALIDATION
    if (!isToday(selectedEvent.date)) {
        setScanResult({
            success: false,
            title: isPast(selectedEvent.date) ? "Event Expired" : "Event Locked",
            message: isPast(selectedEvent.date) 
                ? "This event has already ended. Scanning is disabled." 
                : `This event is scheduled for ${formatDateDisplay(selectedEvent.date)}.`,
            isDateError: true 
        });
        setView('result');
        return;
    }

    setLoading(true);

    try {
      const response = await axios.post(ENDPOINTS.VERIFY_TICKET, { event_id: selectedEvent.id, qr_code: scannedCode });
      const data = response.data;

      if (data.success) {
        setScanResult({ success: true, data: data.student });
      } else {
        setScanResult({ success: false, message: data.message });
      }
      setView('result');
    } catch (error) {
        // Mock fallback
        if(scannedCode.includes("VALID")) {
            setScanResult({ success: true, data: { name: "Demo User", id: "123", course: "B.Tech", image: "https://via.placeholder.com/150"} });
        } else {
            setScanResult({ success: false, message: "Server Error or Invalid Code" });
        }
        setView('result');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${ENDPOINTS.GET_ATTENDEES}?event_id=${selectedEvent.id}`);
      setAttendedList(Array.isArray(response.data) ? response.data : []);
      setView('list');
    } catch (error) {
      setErrorMsg("Failed to fetch list");
    } finally {
      setLoading(false);
    }
  };

  const resetScanner = () => {
    setScanResult(null);
    setPauseScan(false);
    setView('scanner');
  };

  // FILTER LOGIC
  const upcomingEvents = events.filter(e => !isPast(e.date));
  const pastEvents = events.filter(e => isPast(e.date));

  return (
    <div className="dashboard-container">
      {view !== 'history_sheet' && (
          <nav className="navbar">
            <div className="nav-logo">ETHICCRAFT SCANNER</div>
            <div className="nav-right">
                {selectedEvent && view !== 'events' && <div className="event-badge">{selectedEvent.name}</div>}
                <button className="logout-icon-btn" onClick={onLogout}><LogOut size={18} /></button>
            </div>
          </nav>
      )}

      <main className="main-content">
        <AnimatePresence mode='wait'>
          
          {/* VIEW 1: EVENT SELECTION WITH TABS */}
          {view === 'events' && (
            <motion.div key="events" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="view-container">
              
              <div className="header-flex">
                <h1 className="heading">Events</h1>
                {/* PREMIUM TAB SWITCHER */}
                <div className="tab-switcher">
                    <button 
                        className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveTab('upcoming')}
                    >
                        Upcoming
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        History
                    </button>
                </div>
              </div>

              <div className="event-grid">
                {activeTab === 'upcoming' ? (
                    upcomingEvents.length > 0 ? (
                        upcomingEvents.map(event => {
                            const active = isToday(event.date);
                            return (
                                <div key={event.id} className={`event-card ${active ? 'active' : 'inactive'}`} onClick={() => handleEventSelect(event)}>
                                    <div className="card-header">
                                        <h3>{event.name}</h3>
                                        {active ? <div className="live-dot"></div> : <Zap size={16} className="text-muted" />}
                                    </div>
                                    <div className="card-date">
                                        <Calendar size={14} /> {formatDateDisplay(event.date)}
                                    </div>
                                    <div className={`status-text ${active ? 'text-green' : 'text-gold'}`}>
                                        {active ? "Live Now" : "Coming Soon"}
                                    </div>
                                </div>
                            );
                        })
                    ) : <div className="empty-state">No upcoming events.</div>
                ) : (
                    pastEvents.map(event => (
                        <div key={event.id} className="event-card history-card" onClick={() => handleEventSelect(event)}>
                            <div className="card-header">
                                <h3>{event.name}</h3>
                                <div className="sheet-icon-badge"><History size={14}/> Sheet</div>
                            </div>
                            <div className="card-date"><Clock size={14} /> Ended: {formatDateDisplay(event.date)}</div>
                            <div className="status-text text-muted">Tap to view data</div>
                        </div>
                    ))
                )}
              </div>
            </motion.div>
          )}

          {/* VIEW 5: NEW HISTORY SPREADSHEET VIEW */}
          {view === 'history_sheet' && (
              <HistorySpreadsheet event={selectedEvent} onBack={() => setView('events')} />
          )}

          {/* VIEW 2: SCANNER (Updated for Past Events) */}
          {view === 'scanner' && (
            <motion.div key="scanner" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="view-container scanner-view">
              <div className="scanner-frame">
                <div className="scan-line"></div>
                <Scanner onScan={handleScan} styles={{ container: { width: '100%', height: '100%' } }} components={{ audio: false, finder: false }} />
                
                {/* Logic to show LOCKED overlay if date mismatch (Past or Future) */}
                {!isToday(selectedEvent.date) && (
                    <div className="date-warning-overlay">
                        {isPast(selectedEvent.date) ? <History size={40} /> : <Lock size={40} />}
                        <p>{isPast(selectedEvent.date) ? "Event Ended" : "Event Locked"}</p>
                        <span>{isPast(selectedEvent.date) ? "Scanning disabled" : "Check date"}</span>
                    </div>
                )}
                {loading && <div className="scanning-overlay">Verifying...</div>}
              </div>

              <div className="scanner-info-box">
                 <p className="instruction">
                    {isPast(selectedEvent.date) ? "Viewing Archive: " : "Scanning for: "}
                    <strong style={{color: '#D4AF37'}}>{selectedEvent.name}</strong>
                 </p>
                 <div className="date-pill">
                    <Calendar size={14} /> {formatDateDisplay(selectedEvent.date)}
                 </div>
              </div>

              <div className="scanner-actions">
                <button className="secondary-btn" onClick={() => setView('events')}><ArrowLeft size={18} /> Events</button>
                <button className="primary-btn" onClick={handleFetchList}>
                    <Users size={18} /> 
                    {isPast(selectedEvent.date) ? "View Past Data" : "Attendees"}
                </button>
              </div>
            </motion.div>
          )}

          {/* VIEW 3: RESULT (Unchanged) */}
          {view === 'result' && scanResult && (
            <motion.div key="result" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="view-container result-view">
              <div className={`result-card ${scanResult.success ? 'success' : 'failure'}`}>
                <div className="result-icon">
                    {scanResult.success ? <CheckCircle size={60} /> : (scanResult.isDateError ? <Clock size={60} /> : <XCircle size={60} />)}
                </div>
                <h2>{scanResult.title || (scanResult.success ? "Attendance Marked" : "Access Denied")}</h2>
                {scanResult.success ? (
                  <div className="student-details">
                    <h3>{scanResult.data.name}</h3>
                    <p>{scanResult.data.course}</p>
                    <div className="ticket-id">ID: {scanResult.data.id}</div>
                  </div>
                ) : (
                  <div className="error-details">
                      <p className="error-msg">{scanResult.message}</p>
                      {scanResult.isDateError && <p className="sub-error">Go back to view history.</p>}
                  </div>
                )}
                <button className="scan-again-btn" onClick={resetScanner}><RefreshCw size={18} /> Continue</button>
              </div>
            </motion.div>
          )}

           {/* VIEW 4: LIST (Unchanged) */}
           {view === 'list' && (
            <motion.div key="list" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} className="view-container list-view">
              <div className="list-header">
                <button className="back-icon" onClick={() => setView('scanner')}><ArrowLeft /></button>
                <h2>Attendees</h2>
              </div>
              <div className="list-content">
                  {attendedList.map((s, i) => (
                      <div key={i} className="list-item"><h4>{s.name}</h4><span className="status-badge">Present</span></div>
                  ))}
                  {attendedList.length === 0 && <div className="empty-state">No data found.</div>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    if (localStorage.getItem('admin_token')) setIsAuthenticated(true);
  }, []);

  const handleLoginSuccess = () => setIsAuthenticated(true);
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  return (
    <div className="app-root">
      {isAuthenticated ? <ScannerDashboard onLogout={handleLogout} /> : <LoginScreen onLoginSuccess={handleLoginSuccess} />}
    </div>
  );
};

export default App;